import React from 'react'
import Section from '../../common/section/section'
import { MembershipDetailed, MembershipPayment, MembershipPaymentStatus } from 'memberships/schema'
import { formatISOString, DateFormats } from 'utils/dates'
import PermissionRequired from 'admin/permissionRequired'
import ActionButton from 'uiComponents/buttons'
import Currency from 'uiComponents/money/currency'
import { LoaderWrapper } from 'uiComponents/loaders'
import Badge, { BadgeVariant } from 'uiComponents/badge'
import { t, Trans } from 'translations'
import './payments.scss'

const getPaymentStatusMap = (): Record<MembershipPaymentStatus, { text: string; variant: BadgeVariant }> => ({
    [MembershipPaymentStatus.PAID]: {
        text: t('memberships.membershipDetailsPanel.payments.status.paid'),
        variant: 'success',
    },
    [MembershipPaymentStatus.REFUNDED]: {
        text: t('memberships.membershipDetailsPanel.payments.status.refunded'),
        variant: 'info',
    },
    [MembershipPaymentStatus.REFUND_IN_PROGRESS]: {
        text: t('memberships.membershipDetailsPanel.payments.status.refundInProgress'),
        variant: 'info',
    },
    [MembershipPaymentStatus.PENDING]: {
        text: t('memberships.membershipDetailsPanel.payments.status.pending'),
        variant: 'warning',
    },
    [MembershipPaymentStatus.OPEN]: {
        text: t('memberships.membershipDetailsPanel.payments.status.open'),
        variant: 'info',
    },
    [MembershipPaymentStatus.OVERDUE]: {
        text: t('memberships.membershipDetailsPanel.payments.status.overdue'),
        variant: 'warning',
    },
    [MembershipPaymentStatus.FAILED]: {
        text: t('memberships.membershipDetailsPanel.payments.status.failed'),
        variant: 'error',
    },
    [MembershipPaymentStatus.CHARGED_BACK]: {
        text: t('memberships.membershipDetailsPanel.payments.status.chargedBack'),
        variant: 'error',
    },
    [MembershipPaymentStatus.CANCELLED]: {
        text: t('memberships.membershipDetailsPanel.payments.status.cancelled'),
        variant: 'neutral',
    },
})

const PaymentStatus = ({ status, loading }: { status: MembershipPaymentStatus; loading?: boolean }) => {
    const { text, variant } = getPaymentStatusMap()[status]

    return (
        <Badge variant={variant} loading={loading}>
            {text}
        </Badge>
    )
}

interface PaymentBannerProps {
    membership: MembershipDetailed
    accountSlug: string
    openRetryModal: () => void
}

// Says what this membership owes and lets the partner collect it; a retry may cover the whole order.
const PaymentBanner = ({ membership, accountSlug, openRetryModal }: PaymentBannerProps) => {
    const { paymentRetry, payments } = membership
    const today = formatISOString(new Date(), 'yyyy-MM-dd')

    const owed = paymentRetry?.payments.filter((p) => p.membershipUuid === membership.uuid) ?? []

    if (paymentRetry && owed.length) {
        const dueDates = [...new Set(owed.map((p) => p.dueDate))]
        const lastFailed = payments
            .filter((p) => p.status === MembershipPaymentStatus.FAILED)
            .map((p) => p.paymentDate)
            .sort()
            .pop()
        const others = paymentRetry.memberships.filter((m) => m.uuid !== membership.uuid).length
        const amount = (
            <Currency amount={owed.reduce((sum, p) => sum + Number(p.amount), 0)} currency={owed[0]?.currency} />
        )

        return (
            <div className="payment-banner error">
                <div>
                    <strong>
                        <Trans
                            i18nKey="memberships.membershipDetailsPanel.payments.banner.overdue"
                            components={{ amount }}
                        />
                    </strong>
                    <span>
                        {t('memberships.membershipDetailsPanel.payments.banner.overdueDetail', {
                            count: dueDates.length,
                            months: dueDates.map((d) => formatISOString(d, 'MMM')).join(', '),
                        })}
                        {lastFailed &&
                            ` · ${t('memberships.membershipDetailsPanel.payments.banner.lastFailed', {
                                date: formatISOString(lastFailed, DateFormats.SHORT_DATE),
                            })}`}
                    </span>
                    {others > 0 && (
                        <span>
                            {t('memberships.membershipDetailsPanel.payments.banner.otherMemberships', {
                                count: others,
                            })}
                        </span>
                    )}
                </div>
                <PermissionRequired name="edit_orders" accountSlug={accountSlug}>
                    <ActionButton onClick={openRetryModal}>
                        {t('memberships.membershipDetailsPanel.payments.banner.collect')}
                    </ActionButton>
                </PermissionRequired>
            </div>
        )
    }

    const scheduled = membership.status === 'overdue' && payments.find((p) => p.status === MembershipPaymentStatus.OPEN)
    if (!scheduled) return null

    return scheduled.paymentDate > today ? (
        <div className="payment-banner info">
            <div>
                <strong>
                    {t('memberships.membershipDetailsPanel.payments.banner.scheduled', {
                        date: formatISOString(scheduled.paymentDate, DateFormats.SHORT_DATE),
                    })}
                </strong>
            </div>
        </div>
    ) : (
        <div className="payment-banner warning">
            <div>
                <strong>{t('memberships.membershipDetailsPanel.payments.banner.charging')}</strong>
                <span>{t('memberships.membershipDetailsPanel.payments.banner.chargingDetail')}</span>
            </div>
        </div>
    )
}

interface PaymentsSectionProps {
    membership?: MembershipDetailed
    accountSlug: string
    isLoading: boolean
    setPaymentToRefund: (payment: MembershipPayment) => void
    openRetryModal: () => void
}

const PaymentsSection = ({
    membership,
    accountSlug,
    isLoading,
    setPaymentToRefund,
    openRetryModal,
}: PaymentsSectionProps) => {
    const sortedPayments = [...(membership?.payments || [])].sort(
        (a, b) => new Date(b.paymentDate).getTime() - new Date(a.paymentDate).getTime() || Number(b.id) - Number(a.id),
    )
    const upcomingPayment = membership?.upcomingPayment

    return (
        <Section
            title={t('memberships.membershipDetailsPanel.payments.title')}
            className="membership-details-payments-section"
        >
            {membership && !isLoading && (
                <PaymentBanner membership={membership} accountSlug={accountSlug} openRetryModal={openRetryModal} />
            )}
            <table className="membership-details-payments-table">
                <thead>
                    <tr>
                        <th className="status">{t('memberships.membershipDetailsPanel.payments.columns.status')}</th>
                        <th className="amount">{t('memberships.membershipDetailsPanel.payments.columns.amount')}</th>
                        <th className="date">{t('memberships.membershipDetailsPanel.payments.columns.date')}</th>
                        <th className="action" />
                    </tr>
                </thead>
                <tbody>
                    <LoaderWrapper loading={isLoading}>
                        {upcomingPayment && (
                            <tr className="upcoming">
                                <td className="status">
                                    <Badge variant="info" loading={isLoading}>
                                        {t('memberships.membershipDetailsPanel.payments.status.upcoming')}
                                    </Badge>
                                </td>
                                <td className="amount">
                                    <Currency amount={upcomingPayment.amount} currency={upcomingPayment.currency} />
                                </td>
                                <td className="date">
                                    {formatISOString(upcomingPayment.paymentDate, DateFormats.SHORT_DATE)}
                                </td>
                                <td className="action" />
                            </tr>
                        )}
                        {sortedPayments.map((payment) => (
                            <tr key={payment.id}>
                                <td className="status">
                                    <PaymentStatus status={payment.status} loading={isLoading} />
                                </td>
                                <td className="amount">
                                    <Currency amount={payment.amount} currency={payment.currency} />
                                </td>
                                <td className="date">{formatISOString(payment.paymentDate, DateFormats.SHORT_DATE)}</td>
                                <td className="action">
                                    {payment.status === 'PAID' && (
                                        <PermissionRequired name="refund_orders" accountSlug={accountSlug}>
                                            <div className="refund-button" onClick={() => setPaymentToRefund(payment)}>
                                                {t('memberships.membershipDetailsPanel.payments.refund')}
                                            </div>
                                        </PermissionRequired>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </LoaderWrapper>
                </tbody>
            </table>
        </Section>
    )
}

export default PaymentsSection
